import React, { useMemo, useState } from "react";

import { useTheme } from "../../providers/ThemeContext";
import { useI18n } from "../../languages/providers/I18nProvider";

import { ourworks, ourworkKeys } from "./OurWorkContent";
import { GlobalStyles } from "../../styles/GlobalStyles";
import DetailedCard from "../../components/cards/DetailedCard/DetailedCard";
import SEO from "../../components/common/SEO";
import { breadcrumbSchema } from "../../config/seoConfig";
import { OurWorkStyles } from "./OurWorkStyles";
import { useLangPath } from "../../hooks/useLangPath";
import { EID55_PATH } from "../EidCollection55/eidProducts";

const OurWorkPage = () => {
  const [filter, setFilter] = useState<
    "all" | "records" | "government" | "events"
  >("all");

  const { t, lang } = useI18n();

  const { theme } = useTheme();
  const s = OurWorkStyles(theme);
  const globalStyles = GlobalStyles(theme);
  const lp = useLangPath();

  const visible = useMemo(
    () =>
      filter === "all" ? ourworks : ourworks.filter((a) => a.cat === filter),
    [filter],
  );

  return (
    <main style={globalStyles.page}>
      <SEO
        title={t("seo:ourwork:title")}
        description={t("seo:ourwork:description")}
        lang={lang}
        schema={[
          breadcrumbSchema(lang, [
            { name: t("nav:home"), path: "" },
            { name: t("nav:ourwork"), path: "/ourwork" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Creation Arts — Featured Projects",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Cairo Flagpole — Flag for the World's Tallest Flagpole (2021–Present)",
                description:
                  "Creation Arts manufactured the 60 × 40 m Egyptian national flag for the Cairo Flagpole — the world's tallest flagpole at 201 m.",
                url: `https://creationarts.com/${lang}/ourwork/cairo-flagpole-flag`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Jeddah Flagpole — World's Tallest Flag (2014–2021)",
                description:
                  "Creation Arts manufactured the 49.5 × 33 m Saudi national flag for the Jeddah Flagpole — world's tallest flagpole from 2014 to 2021.",
                url: `https://creationarts.com/${lang}/ourwork/jeddah-flagpole-flag`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "84 km Highway Flag Installation — 3,600 Flags in 3 Days",
                description:
                  "Creation Arts installed approximately 3,600 L-shaped flags along an 84-kilometre highway stretch in just 3 days.",
                url: `https://creationarts.com/${lang}/ourwork/highway-flag-campaign`,
              },
              {
                "@type": "ListItem",
                position: 4,
                name: "24-Hour Order Fulfilment",
                description:
                  "All approved orders at Creation Arts are prepared and ready for delivery within 24 hours, 7 days a week.",
                url: `https://creationarts.com/${lang}/ourwork/24hr-order-fulfilment`,
              },
            ],
          },
        ]}
      />
      <section style={s.hero}>
        <h1 style={globalStyles.pageTitle}>{t(ourworkKeys.pageTitle)}</h1>
        <p style={globalStyles.pageSubTitle}>{t(ourworkKeys.pageSubtitle)}</p>
      </section>

      <section style={s.gridWrap}>
        {/* Filter row */}
        <div style={s.filterRow}>
          {(["all", "records", "events"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              style={{
                ...(s.filterBtn as any),
                ...(filter === k ? s.filterBtnActive : {}),
              }}
            >
              {t(ourworkKeys.filters[k])}
            </button>
          ))}
        </div>

        {/* Cards */}
        <div style={globalStyles.cardGrid}>
          {(filter === "all" || filter === "events") && (
            <DetailedCard
              title={t("ourwork:items:eid55:title")}
              image="/eid55/scarves/scarf-3.png"
              yearLabel={t(ourworkKeys.labels.year)}
              year={2026}
              locationLabel={t(ourworkKeys.labels.location)}
              location={t("ourwork:items:eid55:location")}
              blurb={t("ourwork:items:eid55:blurb")}
              to={lp(EID55_PATH)}
            />
          )}
          {visible.map((item) => (
            <DetailedCard
              key={item.id}
              title={t(item.titleKey)}
              image={item.image}
              yearLabel={t(ourworkKeys.labels.year)}
              year={item.year}
              locationLabel={t(ourworkKeys.labels.location)}
              location={t(item.locationKey)}
              blurb={t(item.blurbKey)}
              metrics={item.metrics.map((m) => ({
                label: t(m.labelKey),
                value: m.value,
              }))}
              to={lp(`/ourwork/${item.id}`)}
            />
          ))}
        </div>
      </section>
    </main>
  );
};

export default OurWorkPage;
